// SN76489 Sound Chip Simulator
// The BBC Micro used this TI chip running at 4MHz (though the CPU was 2MHz)
// Frequency calculation: output = 125000 / N where N is the 10-bit divider (1-1023)

interface ToneChannel {
  oscillator: OscillatorNode | null;
  gain: GainNode | null;
  frequency: number;
  volume: number; // 0-15, where 0 is loudest and 15 is off (inverted like real chip)
}

export class SN76489 {
  private audioContext: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private channels: ToneChannel[] = [];

  // BBC Micro clock divider constant
  private static readonly CLOCK_DIVISOR = 125000;

  constructor() {
    this.channels = Array(3).fill(null).map(() => ({
      oscillator: null,
      gain: null,
      frequency: 440,
      volume: 15 // Off by default
    }));
  }

  async init(): Promise<void> {
    if (this.audioContext) return;

    this.audioContext = new AudioContext();
    this.masterGain = this.audioContext.createGain();
    this.masterGain.gain.value = 0.3; // Don't blow anyone's ears out
    this.masterGain.connect(this.audioContext.destination);

    // Initialize the three tone channels
    for (let i = 0; i < 3; i++) {
      const oscillator = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();

      oscillator.type = 'square'; // SN76489 produces square waves
      oscillator.frequency.value = 440;
      gain.gain.value = 0;

      oscillator.connect(gain);
      gain.connect(this.masterGain);
      oscillator.start();

      this.channels[i] = {
        oscillator,
        gain,
        frequency: 440,
        volume: 15
      };
    }
  }

  // Convert 10-bit register value to frequency (as the real chip does)
  private registerToFrequency(registerValue: number): number {
    if (registerValue === 0) return 0;
    return SN76489.CLOCK_DIVISOR / Math.max(1, registerValue);
  }

  // Convert 4-bit volume (0=loud, 15=off) to gain value
  private volumeToGain(volume: number): number {
    if (volume >= 15) return 0;
    // Each step is roughly 2dB attenuation on the real chip
    // This gives a logarithmic response
    return Math.pow(10, -(volume * 2) / 20) * 0.5;
  }

  // Set tone for a channel (0-2) using register value (1-1023)
  setToneRegister(channel: number, registerValue: number): void {
    if (channel < 0 || channel > 2 || !this.channels[channel].oscillator) return;

    const freq = this.registerToFrequency(registerValue);
    this.channels[channel].frequency = freq;
    this.channels[channel].oscillator!.frequency.value = freq;
  }

  // Set frequency directly in Hz (convenience method)
  setFrequency(channel: number, frequency: number): void {
    if (channel < 0 || channel > 2 || !this.channels[channel].oscillator) return;

    this.channels[channel].frequency = frequency;
    this.channels[channel].oscillator!.frequency.value = frequency;
  }

  // Set volume for a channel (0=loudest, 15=off, matching real chip)
  setVolume(channel: number, volume: number): void {
    if (channel < 0 || channel > 2 || !this.channels[channel].gain) return;

    const clampedVolume = Math.max(0, Math.min(15, volume));
    this.channels[channel].volume = clampedVolume;
    this.channels[channel].gain!.gain.value = this.volumeToGain(clampedVolume);
  }

  // Play a note for a duration
  async playNote(channel: number, frequency: number, volume: number, durationMs: number): Promise<void> {
    this.setFrequency(channel, frequency);
    this.setVolume(channel, volume);

    await this.sleep(durationMs);

    this.setVolume(channel, 15); // Off
  }

  // Play a note with envelope (attack, decay, sustain, release)
  async playEnvelope(
    channel: number,
    frequency: number,
    envelope: { attack: number; decay: number; sustain: number; release: number; sustainLevel: number }
  ): Promise<void> {
    const { attack, decay, sustain, release, sustainLevel } = envelope;
    const steps = 8;

    // Attack: volume 15 -> 0 (remember, 0 is loudest)
    for (let i = steps; i >= 0; i--) {
      this.setFrequency(channel, frequency);
      this.setVolume(channel, Math.round((i / steps) * 15));
      await this.sleep(attack / steps);
    }

    // Decay: volume 0 -> sustainLevel
    for (let i = 0; i <= steps; i++) {
      this.setVolume(channel, Math.round((i / steps) * sustainLevel));
      await this.sleep(decay / steps);
    }

    // Sustain
    await this.sleep(sustain);

    // Release: sustainLevel -> 15 (off)
    for (let i = 0; i <= steps; i++) {
      this.setVolume(channel, Math.round(sustainLevel + (i / steps) * (15 - sustainLevel)));
      await this.sleep(release / steps);
    }

    this.setVolume(channel, 15);
  }

  // Generate white noise (for explosions, engine sounds etc)
  async playNoise(durationMs: number, volume: number = 2): Promise<void> {
    if (!this.audioContext || !this.masterGain) return;

    const bufferSize = this.audioContext.sampleRate * (durationMs / 1000);
    const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const data = buffer.getChannelData(0);

    // Generate noise with BBC Micro-like characteristics
    // The SN76489 noise was quite harsh and digital
    for (let i = 0; i < bufferSize; i++) {
      // White noise: random values quantized to simulate the digital nature
      data[i] = (Math.random() > 0.5 ? 1 : -1) * 0.5;
    }

    const source = this.audioContext.createBufferSource();
    const gain = this.audioContext.createGain();

    source.buffer = buffer;
    gain.gain.value = this.volumeToGain(volume);

    source.connect(gain);
    gain.connect(this.masterGain);

    source.start();

    await this.sleep(durationMs);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Silence all channels
  silence(): void {
    for (let i = 0; i < 3; i++) {
      this.setVolume(i, 15);
    }
  }

  // === BBC MICRO CLASSIC SOUNDS ===

  // The iconic two-tone startup beep
  // The BBC Micro startup is "boooop-bip" - a longer low tone followed by a shorter higher beep
  // The first tone is from random data on the bus until the OS initialises
  // The second is the VDU 7 reset beep (pitch ~100 in BBC BASIC scale ≈ 488Hz)
  async startupBeep(): Promise<void> {
    // First tone: longer, lower "boooop" (around 200-250Hz, ~600ms)
    await this.playNote(0, 220, 2, 600);
    await this.sleep(80);
    // Second tone: shorter, higher "bip" (around 488Hz based on default VDU 7, ~150ms)
    await this.playNote(0, 488, 2, 150);
  }

  // Error/invalid key beep
  async errorBeep(): Promise<void> {
    await this.playNote(0, 200, 1, 200);
  }

  // === SPACE ANTICS GAME SOUNDS ===

  // Hyperspace jump - sweeping frequency up then down
  async hyperspaceJump(): Promise<void> {
    const startFreq = 100;
    const endFreq = 2000;
    const steps = 30;

    // Sweep up
    for (let i = 0; i < steps; i++) {
      const freq = startFreq + (endFreq - startFreq) * (i / steps);
      this.setFrequency(0, freq);
      this.setVolume(0, 2);
      await this.sleep(15);
    }

    // Brief silence at apex
    this.setVolume(0, 15);
    await this.sleep(50);

    // Sweep down with different channel for stereo-like effect
    for (let i = steps; i >= 0; i--) {
      const freq = startFreq + (endFreq - startFreq) * (i / steps);
      this.setFrequency(1, freq);
      this.setVolume(1, 3);
      await this.sleep(20);
    }

    this.silence();
  }

  // Laser fire - quick descending tone
  async laserFire(): Promise<void> {
    for (let i = 0; i < 10; i++) {
      this.setFrequency(0, 1500 - i * 100);
      this.setVolume(0, 2 + Math.floor(i / 3));
      await this.sleep(15);
    }
    this.setVolume(0, 15);
  }

  // Explosion - noise with decay
  async explosion(): Promise<void> {
    // Layer noise with a low rumble
    this.setFrequency(0, 50);
    this.setVolume(0, 1);

    await this.playNoise(400, 1);

    // Fade out the rumble
    for (let i = 1; i < 15; i++) {
      this.setVolume(0, i);
      await this.sleep(30);
    }
    this.silence();
  }

  // Docking successful - ascending arpeggio
  async dockingComplete(): Promise<void> {
    const notes = [262, 330, 392, 523]; // C E G C (major chord arpeggio)

    for (const note of notes) {
      await this.playNote(0, note, 3, 100);
      await this.sleep(20);
    }

    // Final chord
    this.setFrequency(0, 523);
    this.setFrequency(1, 392);
    this.setFrequency(2, 330);
    this.setVolume(0, 3);
    this.setVolume(1, 4);
    this.setVolume(2, 5);

    await this.sleep(300);

    // Fade out
    for (let i = 0; i < 10; i++) {
      this.setVolume(0, 3 + i);
      this.setVolume(1, 4 + i);
      this.setVolume(2, 5 + i);
      await this.sleep(30);
    }
    this.silence();
  }

  // Coin/credit collected
  async collectItem(): Promise<void> {
    await this.playNote(0, 880, 3, 50);
    await this.playNote(0, 1760, 2, 100);
  }

  // Alert/warning klaxon
  async alert(): Promise<void> {
    for (let i = 0; i < 3; i++) {
      await this.playNote(0, 440, 1, 150);
      await this.playNote(0, 330, 1, 150);
    }
  }

  // Engine hum (continuous - returns stop function)
  startEngine(): () => void {
    let running = true;
    let phase = 0;

    const engineLoop = async () => {
      while (running) {
        // Pulsing low frequency for engine throb
        const freq = 80 + Math.sin(phase) * 20;
        this.setFrequency(2, freq);
        this.setVolume(2, 6 + Math.floor(Math.sin(phase * 2) * 2));
        phase += 0.1;
        await this.sleep(50);
      }
      this.setVolume(2, 15);
    };

    engineLoop();

    return () => { running = false; };
  }

  // Trading success jingle
  async tradingSuccess(): Promise<void> {
    const melody = [
      { freq: 523, dur: 80 },  // C
      { freq: 659, dur: 80 },  // E
      { freq: 784, dur: 80 },  // G
      { freq: 1047, dur: 200 } // C (octave up)
    ];

    for (const { freq, dur } of melody) {
      await this.playNote(0, freq, 3, dur);
      await this.sleep(20);
    }
  }

  // Trading failure
  async tradingFailure(): Promise<void> {
    await this.playNote(0, 200, 2, 100);
    await this.sleep(50);
    await this.playNote(0, 150, 2, 200);
  }

  // Menu select blip
  async menuSelect(): Promise<void> {
    await this.playNote(0, 1200, 4, 30);
  }

  // Menu confirm
  async menuConfirm(): Promise<void> {
    await this.playNote(0, 800, 3, 40);
    await this.playNote(0, 1200, 3, 60);
  }

  // === ADDITIONAL SPACE ANTICS SOUNDS ===

  // Radar ping - classic sonar-style blip
  async radarPing(): Promise<void> {
    this.setFrequency(0, 1800);
    this.setVolume(0, 3);
    await this.sleep(30);

    // Quick decay
    for (let i = 3; i <= 15; i++) {
      this.setVolume(0, i);
      await this.sleep(20);
    }
  }

  // Shield hit - metallic clang with resonance
  async shieldHit(): Promise<void> {
    // Initial impact
    this.setFrequency(0, 300);
    this.setFrequency(1, 450);
    this.setVolume(0, 1);
    this.setVolume(1, 2);

    await this.sleep(50);

    // Resonant decay with slight pitch drop
    for (let i = 0; i < 15; i++) {
      this.setFrequency(0, 300 - i * 5);
      this.setFrequency(1, 450 - i * 8);
      this.setVolume(0, 1 + i);
      this.setVolume(1, 2 + i);
      await this.sleep(25);
    }
    this.silence();
  }

  // Shield depleted warning
  async shieldDepleted(): Promise<void> {
    for (let i = 0; i < 5; i++) {
      await this.playNote(0, 150, 1, 80);
      await this.sleep(80);
    }
  }

  // Fuel warning - urgent pulsing
  async fuelWarning(): Promise<void> {
    for (let i = 0; i < 4; i++) {
      this.setFrequency(0, 600);
      this.setFrequency(1, 603); // Slight detune for urgency
      this.setVolume(0, 2);
      this.setVolume(1, 3);
      await this.sleep(100);
      this.silence();
      await this.sleep(100);
    }
  }

  // Fuel scoop active - whooshing sound
  async fuelScoop(): Promise<void> {
    for (let cycle = 0; cycle < 3; cycle++) {
      for (let i = 0; i < 20; i++) {
        const freq = 200 + Math.sin(i * 0.5) * 150 + i * 10;
        this.setFrequency(0, freq);
        this.setVolume(0, 4);
        await this.sleep(30);
      }
    }
    this.silence();
  }

  // Missile launch
  async missileLaunch(): Promise<void> {
    // Initial ignition burst
    await this.playNoise(100, 2);

    // Ascending roar
    for (let i = 0; i < 25; i++) {
      this.setFrequency(0, 100 + i * 30);
      this.setVolume(0, 3);
      await this.sleep(20);
    }

    // Fade into distance
    for (let i = 0; i < 15; i++) {
      this.setVolume(0, 3 + i);
      await this.sleep(30);
    }
    this.silence();
  }

  // Missile lock warning
  async missileLock(): Promise<void> {
    for (let i = 0; i < 6; i++) {
      const speed = 150 - i * 20; // Gets faster
      await this.playNote(0, 1000, 2, speed / 2);
      await this.sleep(speed / 2);
    }
    // Solid tone at end
    await this.playNote(0, 1000, 1, 300);
  }

  // ECM jamming active
  async ecmJamming(): Promise<void> {
    for (let i = 0; i < 30; i++) {
      const freq = 500 + Math.random() * 1500;
      this.setFrequency(0, freq);
      this.setFrequency(1, freq + 50);
      this.setVolume(0, 3 + Math.floor(Math.random() * 3));
      this.setVolume(1, 4 + Math.floor(Math.random() * 3));
      await this.sleep(40);
    }
    this.silence();
  }

  // Hull damage
  async hullDamage(): Promise<void> {
    // Heavy crunch
    await this.playNoise(150, 1);

    // Structural stress groans
    for (let i = 0; i < 3; i++) {
      this.setFrequency(0, 80 + i * 10);
      this.setVolume(0, 2);
      await this.sleep(100);
      this.setVolume(0, 5);
      await this.sleep(50);
    }
    this.silence();
  }

  // Cargo scoop
  async cargoScoop(): Promise<void> {
    // Mechanical whirr
    for (let i = 0; i < 15; i++) {
      this.setFrequency(0, 200 + i * 20);
      this.setVolume(0, 4);
      await this.sleep(30);
    }
    // Clunk at end
    await this.playNote(1, 100, 2, 80);
    this.silence();
  }

  // Cargo collected
  async cargoCollected(): Promise<void> {
    await this.playNote(0, 600, 3, 60);
    await this.playNote(0, 800, 3, 60);
    await this.playNote(0, 1000, 3, 100);
  }

  // Contraband detected
  async contrabandAlert(): Promise<void> {
    for (let i = 0; i < 3; i++) {
      await this.playNote(0, 200, 1, 150);
      await this.playNote(0, 400, 1, 150);
    }
  }

  // Police scanner
  async policeScanner(): Promise<void> {
    for (let i = 0; i < 20; i++) {
      const freq = 1000 + Math.sin(i * 0.8) * 500;
      this.setFrequency(0, freq);
      this.setVolume(0, 4);
      await this.sleep(50);
    }
    this.silence();
  }

  // Comms incoming
  async commsIncoming(): Promise<void> {
    for (let i = 0; i < 3; i++) {
      await this.playNote(0, 880, 4, 100);
      await this.playNote(0, 1100, 4, 100);
      await this.sleep(200);
    }
  }

  // Comms static
  async commsStatic(): Promise<void> {
    for (let i = 0; i < 10; i++) {
      await this.playNoise(30, 5 + Math.floor(Math.random() * 5));
      await this.sleep(20);
    }
  }

  // Station announcement chime
  async stationChime(): Promise<void> {
    const notes = [523, 659, 784, 659]; // C E G E
    for (const note of notes) {
      await this.playNote(0, note, 4, 150);
      await this.sleep(30);
    }
  }

  // Witch space tunnel (extended hyperspace sequence)
  async witchSpace(): Promise<void> {
    // Eerie warbling
    for (let i = 0; i < 40; i++) {
      const base = 200 + i * 5;
      this.setFrequency(0, base + Math.sin(i * 0.5) * 100);
      this.setFrequency(1, base * 1.5 + Math.cos(i * 0.3) * 150);
      this.setFrequency(2, base * 0.5 + Math.sin(i * 0.7) * 50);
      this.setVolume(0, 5);
      this.setVolume(1, 6);
      this.setVolume(2, 7);
      await this.sleep(60);
    }

    // Exit flash
    this.setVolume(0, 2);
    this.setVolume(1, 2);
    this.setVolume(2, 2);
    await this.sleep(100);

    this.silence();
  }

  // Galactic hyperdrive
  async galacticHyperdrive(): Promise<void> {
    // Building charge
    for (let i = 0; i < 30; i++) {
      this.setFrequency(0, 100 + i * 50);
      this.setFrequency(1, 150 + i * 50);
      this.setVolume(0, 8 - Math.floor(i / 5));
      this.setVolume(1, 9 - Math.floor(i / 5));
      await this.sleep(50);
    }

    // Massive burst
    await this.playNoise(200, 1);

    // Reality tear
    for (let i = 0; i < 20; i++) {
      this.setFrequency(0, 2000 - i * 80);
      this.setVolume(0, 2 + Math.floor(i / 3));
      await this.sleep(40);
    }

    this.silence();
  }

  // Tractor beam
  async tractorBeam(): Promise<void> {
    for (let cycle = 0; cycle < 25; cycle++) {
      this.setFrequency(0, 300 + (cycle % 10) * 20);
      this.setFrequency(1, 305 + (cycle % 10) * 20); // Slight detune for wobble
      this.setVolume(0, 4);
      this.setVolume(1, 5);
      await this.sleep(60);
    }
    this.silence();
  }

  // Energy bomb
  async energyBomb(): Promise<void> {
    // Charging
    for (let i = 0; i < 20; i++) {
      this.setFrequency(0, 200 + i * 100);
      this.setVolume(0, 6 - Math.floor(i / 4));
      await this.sleep(40);
    }

    // BOOM - all channels
    this.setFrequency(0, 80);
    this.setFrequency(1, 120);
    this.setFrequency(2, 60);
    this.setVolume(0, 0);
    this.setVolume(1, 0);
    this.setVolume(2, 1);

    await this.playNoise(300, 0);

    // Rumbling decay
    for (let i = 0; i < 20; i++) {
      this.setVolume(0, i);
      this.setVolume(1, i);
      this.setVolume(2, i);
      await this.sleep(50);
    }
    this.silence();
  }

  // Mining laser
  async miningLaser(): Promise<void> {
    for (let burst = 0; burst < 3; burst++) {
      for (let i = 0; i < 10; i++) {
        this.setFrequency(0, 400 + Math.random() * 200);
        this.setVolume(0, 3);
        await this.sleep(20);
      }
      await this.sleep(50);
    }
    this.silence();
  }

  // Asteroid break
  async asteroidBreak(): Promise<void> {
    // Crack
    await this.playNoise(80, 2);

    // Fragments
    for (let i = 0; i < 5; i++) {
      this.setFrequency(0, 150 + Math.random() * 100);
      this.setVolume(0, 4 + i);
      await this.sleep(60);
    }
    this.silence();
  }

  // Bounty awarded
  async bountyAwarded(): Promise<void> {
    const fanfare = [392, 523, 659, 784, 1047]; // G C E G C
    for (const note of fanfare) {
      await this.playNote(0, note, 3, 80);
      await this.sleep(20);
    }
    // Final flourish
    this.setFrequency(0, 1047);
    this.setFrequency(1, 784);
    this.setVolume(0, 3);
    this.setVolume(1, 4);
    await this.sleep(300);
    this.silence();
  }

  // Legal status changed
  async legalStatusChange(): Promise<void> {
    await this.playNote(0, 300, 2, 200);
    await this.sleep(100);
    await this.playNote(0, 250, 2, 300);
  }

  // Rank up
  async rankUp(): Promise<void> {
    const melody = [
      { freq: 523, dur: 100 },
      { freq: 523, dur: 100 },
      { freq: 523, dur: 100 },
      { freq: 784, dur: 400 }
    ];

    for (const { freq, dur } of melody) {
      await this.playNote(0, freq, 2, dur);
      await this.sleep(50);
    }
  }

  // Purchase complete
  async purchaseComplete(): Promise<void> {
    await this.playNote(0, 800, 4, 50);
    await this.playNote(0, 1000, 3, 50);
    await this.playNote(0, 1200, 3, 100);
  }

  // Insufficient funds
  async insufficientFunds(): Promise<void> {
    await this.playNote(0, 200, 3, 100);
    await this.sleep(50);
    await this.playNote(0, 150, 3, 200);
  }

  // Equipment installed
  async equipmentInstalled(): Promise<void> {
    // Mechanical clunks
    await this.playNote(0, 150, 2, 60);
    await this.sleep(40);
    await this.playNote(0, 200, 2, 60);
    await this.sleep(40);
    // Confirmation beep
    await this.playNote(1, 1000, 3, 100);
  }

  // Ship systems online
  async systemsOnline(): Promise<void> {
    const bootSequence = [200, 400, 600, 800, 1000];
    for (let i = 0; i < bootSequence.length; i++) {
      await this.playNote(0, bootSequence[i], 5, 80);
      await this.sleep(100);
    }
    // Final confirmation
    await this.sleep(200);
    await this.playNote(0, 1200, 3, 150);
  }

  // Navigation computer
  async navComputer(): Promise<void> {
    // Computing sounds
    for (let i = 0; i < 8; i++) {
      await this.playNote(0, 1500 + (i % 2) * 200, 6, 30);
      await this.sleep(50);
    }
    // Confirmation
    await this.playNote(0, 880, 4, 100);
  }

  // Thargoid presence
  async thargoidPresence(): Promise<void> {
    // Alien, unsettling drone
    for (let i = 0; i < 30; i++) {
      this.setFrequency(0, 150 + Math.sin(i * 0.3) * 50);
      this.setFrequency(1, 200 + Math.cos(i * 0.5) * 80);
      this.setFrequency(2, 100 + Math.sin(i * 0.2) * 30);
      this.setVolume(0, 4);
      this.setVolume(1, 5);
      this.setVolume(2, 6);
      await this.sleep(80);
    }
    this.silence();
  }

  // Thargoid attack
  async thargoidAttack(): Promise<void> {
    // Alien weapon sound - weird warbling
    for (let i = 0; i < 15; i++) {
      const baseFreq = 800 + Math.sin(i * 2) * 400;
      this.setFrequency(0, baseFreq);
      this.setFrequency(1, baseFreq * 1.1);
      this.setVolume(0, 2);
      this.setVolume(1, 3);
      await this.sleep(30);
    }
    this.silence();
  }

  // Self destruct warning
  async selfDestructWarning(): Promise<void> {
    for (let i = 0; i < 5; i++) {
      // Alternating alarm
      this.setFrequency(0, 800);
      this.setFrequency(1, 600);
      this.setVolume(0, 1);
      this.setVolume(1, 2);
      await this.sleep(150);

      this.setFrequency(0, 600);
      this.setFrequency(1, 800);
      await this.sleep(150);
    }
    this.silence();
  }

  // Escape pod launch
  async escapePod(): Promise<void> {
    // Explosive release
    await this.playNoise(100, 2);

    // Pod engine ascending
    for (let i = 0; i < 20; i++) {
      this.setFrequency(0, 200 + i * 40);
      this.setVolume(0, 3 + Math.floor(i / 5));
      await this.sleep(40);
    }
    this.silence();
  }

  // Cloaking device
  async cloakingDevice(): Promise<void> {
    // Phasing out sound
    for (let i = 0; i < 20; i++) {
      this.setFrequency(0, 1000 - i * 40);
      this.setFrequency(1, 1010 - i * 40);
      this.setVolume(0, 3 + Math.floor(i / 3));
      this.setVolume(1, 4 + Math.floor(i / 3));
      await this.sleep(50);
    }
    this.silence();
  }

  // Docking computer jingle (Blue Danube style simplified)
  async dockingComputerJingle(): Promise<void> {
    const melody = [
      { freq: 440, dur: 300 },  // A
      { freq: 440, dur: 150 },
      { freq: 659, dur: 450 },  // E
      { freq: 554, dur: 300 },  // C#
      { freq: 554, dur: 150 },
      { freq: 880, dur: 450 },  // A
    ];

    for (const { freq, dur } of melody) {
      await this.playNote(0, freq, 4, dur);
      await this.sleep(30);
    }
  }

  // Typewriter effect for text
  async typewriterKey(): Promise<void> {
    await this.playNote(0, 800 + Math.random() * 400, 8, 15);
  }

  // Typewriter return
  async typewriterReturn(): Promise<void> {
    this.setFrequency(0, 300);
    this.setVolume(0, 5);
    await this.sleep(30);
    this.setFrequency(0, 200);
    await this.sleep(50);
    this.silence();
  }

  // Game over
  async gameOver(): Promise<void> {
    const notes = [440, 392, 349, 330, 294, 262]; // Descending

    for (const note of notes) {
      await this.playNote(0, note, 3, 200);
      await this.sleep(50);
    }

    // Final low drone
    this.setFrequency(0, 100);
    this.setFrequency(1, 103);
    this.setVolume(0, 3);
    this.setVolume(1, 4);
    await this.sleep(500);

    for (let i = 0; i < 15; i++) {
      this.setVolume(0, 3 + i);
      this.setVolume(1, 4 + i);
      await this.sleep(50);
    }
    this.silence();
  }

  // Title screen music loop (returns stop function)
  startTitleMusic(): () => void {
    let running = true;

    const musicLoop = async () => {
      const bassLine = [131, 165, 196, 165]; // C3 E3 G3 E3
      const melody = [523, 659, 784, 659, 523, 392, 523, 659]; // C5 E5 G5...

      while (running) {
        for (let bar = 0; bar < 4 && running; bar++) {
          // Bass note
          this.setFrequency(2, bassLine[bar]);
          this.setVolume(2, 6);

          for (let beat = 0; beat < 2 && running; beat++) {
            const melodyNote = melody[(bar * 2 + beat) % melody.length];
            this.setFrequency(0, melodyNote);
            this.setVolume(0, 4);
            await this.sleep(250);
            this.setVolume(0, 8);
            await this.sleep(250);
          }
        }
      }
      this.silence();
    };

    musicLoop();

    return () => { running = false; };
  }
}
